import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Logo } from "./Logo";
import { NAV_LINKS, BUSINESS } from "../data";
import { scrollToId } from "../hooks/useSmoothScroll";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  const handleNav = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 450);
    } else {
      scrollToId(id);
    }
  };

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-forest/85 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-5 md:px-10 h-20 flex items-center justify-between">
          <button data-testid="logo-home" onClick={() => handleNav("home")} className="focus:outline-none">
            <Logo />
          </button>

          <div className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => handleNav(l.id)}
                className="relative text-sand/80 text-sm tracking-wide hover:text-sand transition-colors duration-300 group"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-terracotta transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </div>

          <a
            href={BUSINESS.phoneHref}
            data-testid="nav-phone"
            className="hidden lg:flex items-center gap-2 bg-terracotta text-white px-5 py-2.5 text-sm tracking-wide hover:bg-terracotta-dark transition-colors duration-300"
          >
            <Phone size={15} /> {BUSINESS.phone}
          </a>

          <button
            data-testid="hamburger-btn"
            onClick={() => setOpen(true)}
            className="lg:hidden text-sand p-2 -mr-2"
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 z-[55] bg-black/60 backdrop-blur-sm lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              data-testid="tray-overlay"
            />
            <motion.aside
              data-testid="mobile-tray"
              className="fixed inset-y-0 right-0 z-[60] w-[82%] max-w-sm bg-forest border-l border-white/10 flex flex-col lg:hidden"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="flex items-center justify-between px-7 h-20 border-b border-white/10">
                <Logo />
                <button
                  data-testid="tray-close-btn"
                  onClick={() => setOpen(false)}
                  className="text-sand p-2 -mr-2"
                  aria-label="Close menu"
                >
                  <X size={26} />
                </button>
              </div>
              <div className="flex-1 flex flex-col justify-center px-9 gap-1">
                {NAV_LINKS.map((l, i) => (
                  <motion.button
                    key={l.id}
                    data-testid={`tray-link-${l.id}`}
                    onClick={() => handleNav(l.id)}
                    className="text-left font-display text-4xl text-sand py-2 hover:text-terracotta transition-colors duration-300"
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 + i * 0.07, duration: 0.5 }}
                  >
                    {l.label}
                  </motion.button>
                ))}
              </div>
              <a
                href={BUSINESS.phoneHref}
                className="m-7 flex items-center justify-center gap-2 bg-terracotta text-white py-4 tracking-wide"
                data-testid="tray-phone"
              >
                <Phone size={16} /> {BUSINESS.phone}
              </a>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
